import useCartContext from "../hooks/useCartContext";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";

const ShoppingCart = () => {
  const { cartItems, removeFromCart, increaseQuantity, decreaseQuantity } =
    useCartContext();

  // Helper function to safely convert to number
  const toNumber = (value) => {
    if (typeof value === "number") return value;
    if (typeof value === "string") {
      // Remove any non-numeric characters except decimal point
      const numStr = value.replace(/[^0-9.]/g, "");
      return parseFloat(numStr) || 0;
    }
    return 0;
  };

  // Safely calculate the total amount
  const totalAmount = cartItems.reduce((total, item) => {
    const price = toNumber(item.price);
    const quantity = item.quantity || 1;
    return total + price * quantity;
  }, 0);

  // Calculate total items
  const totalItems = cartItems.reduce(
    (acc, item) => acc + (item.quantity || 1),
    0
  );

  const processPayment = () => {
    Swal.fire({
      title: "Payment Processing",
      text: "We're redirecting you to our secure checkout system...",
      icon: "info",
      timer: 2000,
      showConfirmButton: false,
      willClose: () => {
        Swal.fire({
          title: "Sorry!",
          text: "We are still working on this feature.",
          icon: "info",
        });
      },
    });
  };

  // Helper function to safely format prices
  const formatPrice = (price) => {
    const num = toNumber(price);
    return isNaN(num) ? "$0.00" : `$${num.toFixed(2)}`;
  };

  return (
    <div className="bg-light min-vh-100 py-5">
      <div className="container">
        {/* Header Section */}
        <div className="row mb-4">
          <div className="col-12">
            <nav aria-label="breadcrumb">
              <ol className="breadcrumb">
                <li className="breadcrumb-item">
                  <Link to="/" className="text-decoration-none">
                    <i className="fa fa-home"></i> Home
                  </Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  Shopping Cart
                </li>
              </ol>
            </nav>
            <h1 className="display-5 fw-bold">
              <i className="fa fa-shopping-cart text-primary me-2"></i>
              Your Shopping Cart
            </h1>
            <p className="text-muted">
              {cartItems.length === 0
                ? "Your cart is currently empty"
                : `You have ${totalItems} ${
                    totalItems === 1 ? "item" : "items"
                  } in your cart`}
            </p>
          </div>
        </div>

        <div className="row g-4">
          {/* Left Column - Cart Items */}
          <div className="col-lg-8">
            {cartItems.length === 0 ? (
              <div className="card shadow-sm border-0">
                <div className="card-body text-center py-5">
                  <i className="fa fa-shopping-cart fa-4x text-muted mb-4"></i>
                  <h3 className="mb-3">Your cart is empty</h3>
                  <p className="text-muted mb-4">
                    Looks like you haven't added anything to your cart yet
                  </p>
                  <Link
                    to="/api/courses"
                    className="btn btn-primary btn-lg px-4"
                  >
                    <i className="fa fa-book me-2"></i> Browse Courses
                  </Link>
                </div>
              </div>
            ) : (
              <div className="card shadow-sm border-0">
                <div className="card-header bg-white border-0 py-3">
                  <h5 className="mb-0">
                    <i className="fa fa-list-alt me-2 text-primary"></i>
                    Cart Items ({totalItems})
                  </h5>
                </div>
                <div className="card-body p-0">
                  <div className="list-group list-group-flush">
                    {cartItems.map((item) => {
                      const price = toNumber(item.price);
                      const quantity = item.quantity || 1;
                      const itemTotal = price * quantity;

                      return (
                        <div
                          key={item.id}
                          className="list-group-item border-0 border-bottom"
                        >
                          <div className="row align-items-center g-3">
                            <div className="col-md-2 d-none d-md-block">
                              <div className="bg-light rounded p-2 text-center">
                                <i className="fa fa-book fa-3x text-muted"></i>
                              </div>
                            </div>
                            <div className="col-md-6 col-12">
                              <h5 className="mb-1">{item.title}</h5>
                              <p className="text-muted mb-2">
                                {formatPrice(price)}
                              </p>
                              <div className="d-flex align-items-center">
                                <button
                                  className="btn btn-outline-secondary btn-sm me-2"
                                  onClick={() => decreaseQuantity(item)}
                                  disabled={quantity <= 1}
                                >
                                  <i className="fa fa-minus"></i>
                                </button>
                                <span className="fw-bold">{quantity}</span>
                                <button
                                  className="btn btn-outline-secondary btn-sm ms-2"
                                  onClick={() => increaseQuantity(item)}
                                >
                                  <i className="fa fa-plus"></i>
                                </button>
                              </div>
                            </div>
                            <div className="col-md-3 col-6 text-md-center">
                              <h5 className="mb-0 text-success">
                                {formatPrice(itemTotal)}
                              </h5>
                            </div>
                            <div className="col-md-1 col-6 text-end">
                              <button
                                className="btn btn-link text-danger"
                                onClick={() => removeFromCart(item)}
                                title="Remove item"
                              >
                                <i className="fa fa-trash"></i>
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Right Column - Order Summary */}
          {cartItems.length > 0 && (
            <div className="col-lg-4">
              <div
                className="card shadow-sm border-0 sticky-top"
                style={{ top: "20px" }}
              >
                <div className="card-header bg-white border-0 py-3">
                  <h5 className="mb-0">
                    <i className="fa fa-receipt me-2 text-primary"></i>
                    Order Summary
                  </h5>
                </div>
                <div className="card-body">
                  <div className="d-flex justify-content-between mb-3">
                    <span className="text-muted">Subtotal:</span>
                    <span>{formatPrice(totalAmount)}</span>
                  </div>
                  <div className="d-flex justify-content-between mb-3">
                    <span className="text-muted">Discount:</span>
                    <span className="text-success">$0.00</span>
                  </div>
                  <div className="d-flex justify-content-between mb-3">
                    <span className="text-muted">Tax:</span>
                    <span>$0.00</span>
                  </div>
                  <hr />
                  <div className="d-flex justify-content-between mb-4">
                    <h5 className="mb-0">Total:</h5>
                    <h4 className="text-success">{formatPrice(totalAmount)}</h4>
                  </div>
                  <button
                    className="btn btn-primary btn-lg w-100 mb-3"
                    onClick={processPayment}
                  >
                    <i className="fa fa-credit-card me-2"></i> Proceed to
                    Checkout
                  </button>
                  <Link
                    to="/api/courses"
                    className="btn btn-outline-secondary btn-lg w-100"
                  >
                    <i className="fa fa-arrow-left me-2"></i> Continue Shopping
                  </Link>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Empty Cart Recommendation */}
        {cartItems.length === 0 && (
          <div className="row mt-5">
            <div className="col-12">
              <h4 className="mb-4">You might be interested in:</h4>
              <div className="row g-4">
                {[1, 2, 3].map((item) => (
                  <div className="col-md-4" key={item}>
                    <div className="card shadow-sm h-100">
                      <div className="card-body text-center">
                        <div className="bg-light rounded p-3 mb-3">
                          <i className="fa fa-book fa-3x text-primary"></i>
                        </div>
                        <h5>Recommended Course {item}</h5>
                        <p className="text-muted">$99.99</p>
                        <button className="btn btn-outline-primary w-100">
                          <i className="fa fa-plus me-2"></i> Add to Cart
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ShoppingCart;
